import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { Loading } from '~/components';
import { Empty as TableEmpty, tableDefaults } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { ACHBatchRecordErrorBadge, TransferAmount, TransferTypeBadge } from '~/lib/transfers';
import { type BadgeTone, getTransferBadgeClassName } from '~/lib/transfers/utils';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useTableConfig } from '~/stores/Config';
import { useSessionStore } from '~/stores/Session';
import { ACHTransferBatchRecord } from '~/typings/API';

import { TransferDetailsSheet } from './TransferDetailsSheet';
import { TRANSFER_STATUS_META, transferUrlFor } from './transferHelpers';

const POLL_MS = 5000;

const POLLING_BATCH_STATUSES = new Set(['validating', 'processing']);

const PARSING_BATCH_STATUSES = new Set(['pending', 'validating']);

// Map a transfer status's chip color onto the badge tone, so the Status column keeps its semantic color
// while adopting the Type column's badge styling.
const STATUS_BADGE_TONE: Record<string, BadgeTone> = {
  default: 'secondary',
  info: 'info',
  success: 'success',
  danger: 'danger',
};

const columns: DataTableColumnDef<ACHTransferBatchRecord>[] = [
  {
    header: 'Amount / Description',
    accessor: 'amount',
    width: 'minmax(200px, auto)',
    className: 'min-w-0',
    cell: ({ row, value }) => (
      <TransferAmount
        amount={value}
        currencyCode={row.currency}
        description={row.description || undefined}
        isAdding={row.type === 'debit'}
      />
    ),
  },
  {
    header: 'Receiver',
    accessor: 'receiverName',
    width: 'minmax(min-content, 360px)',
    className: 'min-w-0',
    cell: ({ row, value }) => (
      <span className="flex min-w-0 items-baseline gap-2 whitespace-nowrap">
        <span className="text-sm font-medium">{value || '-'}</span>
        {row.companyName && <span className="text-xs text-muted-foreground">{row.companyName}</span>}
      </span>
    ),
  },
  {
    header: 'Type',
    accessor: 'type',
    width: 'minmax(min-content, 150px)',
    cell: ({ value }) => <TransferTypeBadge type={value === 'debit' ? 'ach_debit' : 'ach_credit'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'max-content',
    cell: ({ value }) => {
      const meta = TRANSFER_STATUS_META[value] ?? {
        type: 'default' as const,
        label: value,
        icon: <Icon.CircleBlank />,
      };
      const tone = STATUS_BADGE_TONE[meta.type ?? 'default'] ?? 'secondary';
      return (
        <Badge variant="ghost" className={getTransferBadgeClassName(tone)}>
          {meta.icon}
          {meta.label}
        </Badge>
      );
    },
  },
  {
    header: 'Error',
    accessor: 'error',
    width: 'min-content',
    cell: ({ row }) => <ACHBatchRecordErrorBadge error={row.error} />,
  },
];

const STATUS_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  ...Object.entries(TRANSFER_STATUS_META).map(([value, { label }]) => ({ label, value })),
];

// Bucket so transitions within a phase don't bust the transfers query key.
type TransfersPhase = 'in-flight' | 'post-validation' | 'post-creation';
const TRANSFERS_PHASE: Record<string, TransfersPhase> = {
  validated: 'post-validation',
  canceled: 'post-validation',
  expired: 'post-validation',
  completed: 'post-creation',
  failed: 'post-creation',
};

interface AchBatchTransfersTableProps {
  batchId: string;
  batchStatus: string;
  statusFilter: string;
  /** Fired once the first records fetch resolves, so the caller can defer animating the table in until it has data. */
  onReady?: () => void;
}

export const AchBatchTransfersTable: React.FC<AchBatchTransfersTableProps> = ({
  batchId,
  batchStatus,
  statusFilter,
  onReady,
}) => {
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const navigate = useNavigate();
  const { pageSize: savedPageSize, update: updateTableConfig } = useTableConfig('achBatchRecords');
  const pageSize = Number(savedPageSize);
  const { cursor, onNext, reset, ...pagination } = usePaginationState({ defaultPageSize: pageSize });

  const [prevFilter, setPrevFilter] = useState(statusFilter);
  let effectiveCursor: string | undefined = cursor;
  if (prevFilter !== statusFilter) {
    setPrevFilter(statusFilter);
    reset();
    effectiveCursor = undefined;
  }

  const { data, isLoading, isPlaceholderData, isError } = useQuery({
    queryKey: [
      'ach-batch-records',
      platformId,
      isSandbox,
      batchId,
      effectiveCursor,
      pageSize,
      statusFilter,
      TRANSFERS_PHASE[batchStatus] ?? 'in-flight',
    ],
    queryFn: () =>
      AchBatchRepository.getRecords(batchId, {
        limit: pageSize,
        startingAfter: effectiveCursor,
        status: statusFilter || undefined,
      }),
    enabled: !!batchId,
    refetchInterval: () => (POLLING_BATCH_STATUSES.has(batchStatus) ? POLL_MS : false),
    // A batchStatus phase change (see TRANSFERS_PHASE) starts a fresh query; keep the prior transfers
    // rendered through it so the table doesn't collapse to its loading state and remount.
    placeholderData: keepPreviousData,
  });

  const transfers = data?.records ?? [];
  const isAwaitingRecords = PARSING_BATCH_STATUSES.has(batchStatus) && transfers.length === 0 && !isError;

  // The Error column only earns its space when something actually failed, so hide it until a record on
  // this page carries an error.
  const visibleColumns = React.useMemo(
    () => (transfers.some((t) => t.error) ? columns : columns.filter((column) => column.accessor !== 'error')),
    [transfers]
  );

  // Tell the caller once real records for the current batch have loaded so it can hold the reveal
  // animation until the rows are ready. Excludes keepPreviousData's placeholder (which would otherwise
  // read as "ready" while the new batch is still fetching). Kept in a ref so a changing onReady
  // identity doesn't retrigger it. Also fire on a records-fetch error so the section still reveals (with
  // the empty/error state and the reset affordance) instead of dead-ending on an empty header.
  const hasData = data !== undefined && !isPlaceholderData;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    if (hasData || isError) {
      onReadyRef.current?.();
    }
  }, [hasData, isError]);

  const [openTransferId, setOpenTransferId] = useState<string | null>(null);
  const openTransfer = openTransferId ? (transfers.find((t) => t.id === openTransferId) ?? null) : null;

  // A record with an achTransferId has been initiated, so a real transfer page exists — go there.
  // Otherwise the record is only queued/validated, so preview it in the sheet.
  const openRow = useCallback(
    (transfer: ACHTransferBatchRecord) => {
      const url = transferUrlFor(transfer);
      if (url) {
        navigate(url);
      } else {
        setOpenTransferId(transfer.id);
      }
    },
    [navigate]
  );

  const rowAction = useCallback(
    (transfer: ACHTransferBatchRecord) => {
      const hasTransfer = !!transferUrlFor(transfer);
      return (
        <Button
          onClick={(event: React.SyntheticEvent) => {
            event.stopPropagation();
            openRow(transfer);
          }}
          icon={<Icon.ChevronRightLarge />}
          iconRight
          size="tiny"
          variant="secondary"
        >
          {hasTransfer ? 'View' : 'Preview'}
        </Button>
      );
    },
    [openRow]
  );

  // Only show pagination once the transfers spill past one page (>50) — either there's a next page or
  // we've already navigated forward.
  const showPagination = (data?.hasMore ?? false) || !!effectiveCursor;

  return (
    <>
      <Loading isLoading={isLoading} minHeight={false}>
        <DataTable<ACHTransferBatchRecord>
          {...tableDefaults}
          rowClassName="min-h-11 [&>[data-slot=data-table-action]]:h-11"
          columns={visibleColumns}
          data={transfers}
          pagination={
            showPagination
              ? {
                  hasNext: data?.hasMore ?? false,
                  onNext: () => (transfers.length ? onNext({ cursor: transfers[transfers.length - 1].id }) : undefined),
                  ...pagination,
                  pageSize,
                  onPageSizeChange: (value: number) => {
                    reset();
                    updateTableConfig({ limit: value });
                  },
                }
              : undefined
          }
          isLoading={isLoading || isPlaceholderData || isAwaitingRecords}
          rowAction={rowAction}
          rowKey={(row) => row.id}
          onRowClick={openRow}
          emptyContent={
            isError ? (
              <TableEmpty
                icon={<Icon.Warning />}
                title="Failed to load transfers"
                description="Couldn't load this batch's transfers. Reset and re-upload the file to try again."
              />
            ) : (
              <TableEmpty
                icon={<Icon.AnimationArrows />}
                title="No entries found"
                description={statusFilter ? 'No entries were found matching the specified filters' : undefined}
              />
            )
          }
        />
      </Loading>
      <TransferDetailsSheet
        transfer={openTransfer}
        open={!!openTransferId}
        onOpenChange={(open) => !open && setOpenTransferId(null)}
      />
    </>
  );
};

export { STATUS_FILTER_OPTIONS };
